// Shared thread + composer for both concierge surfaces (the client's "Mensajes
// con tu gerente" and the operator console's "Mensajes"). Presentation only:
// the parent owns the data (messages, send) and passes an onSend callback.
//
// Messages are distinguished by side, not colour alone: the current user's own
// messages sit right (brand blue), the counterpart's sit left (grey), and every
// bubble carries an author + timestamp label.

import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { type ConciergeMessage, formatConciergeTime } from './concierge-data.js'

interface ConciergeThreadProps {
  messages: ConciergeMessage[]
  /** Which side "I" am. My messages render as outgoing (right); the other side incoming (left). */
  mySide: 'manager' | 'client'
  /** Header: who this thread is with. */
  headerName: string
  /** When set, the header name links there (the operator side points at
   *  the client's console page; the client side has nowhere to point). */
  headerHref?: string
  headerSubtitle: string
  headerInitials: string
  /** Shown in place of the thread when there are no messages yet. */
  emptyText: string
  onSend: (body: string) => void
  sending: boolean
  /** Disable the composer (e.g. no manager assigned yet). */
  disabled?: boolean
  placeholder?: string
  /** Mobile-only "back to list" affordance. When set, a chevron shows below md. */
  onBack?: () => void
}

function MessageRow({
  message,
  outgoing,
  receipt,
}: {
  message: ConciergeMessage
  outgoing: boolean
  /** WhatsApp-style status shown under my last message: read ("Visto") or just sent ("Enviado"). */
  receipt?: 'read' | 'sent' | undefined
}): JSX.Element {
  return (
    <div className={outgoing ? 'ml-auto max-w-[80%]' : 'mr-auto max-w-[80%]'}>
      <div className={`mx-1 mb-1 text-[10px] text-text-2 ${outgoing ? 'text-right' : 'text-left'}`}>
        <b className="font-semibold text-text">{message.authorName}</b> ·{' '}
        {formatConciergeTime(message.createdAt)}
      </div>
      <div
        className={`inline-block whitespace-pre-wrap rounded-[14px] px-3 py-[9px] text-left text-[12.5px] leading-relaxed [overflow-wrap:anywhere] ${
          outgoing
            ? 'rounded-br-[5px] bg-[linear-gradient(140deg,#1d4fd1,#1538b8)] text-white'
            : 'rounded-bl-[5px] bg-field text-text'
        }`}
      >
        {message.body}
      </div>
      {receipt && (
        <div className="mx-1 mt-1 text-right text-[11px] text-text-3">
          {receipt === 'read' ? 'Visto' : 'Enviado'}
        </div>
      )}
    </div>
  )
}

/**
 * Presentation-only thread + composer shared by both concierge surfaces; the
 * parent owns the data and passes onSend. `mySide` decides which messages
 * render outgoing; Enter sends (Shift+Enter for a newline), and only the last
 * own message carries a Visto/Enviado receipt.
 */
export function ConciergeThread({
  messages,
  mySide,
  headerName,
  headerHref,
  headerSubtitle,
  headerInitials,
  emptyText,
  onSend,
  sending,
  disabled = false,
  placeholder = 'Escribe un mensaje…',
  onBack,
}: ConciergeThreadProps): JSX.Element {
  const [text, setText] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  // Keep the newest message in view as the thread grows (new sends or polled-in
  // replies), but don't yank the view if the user has scrolled up to read back.
  const lastMessageId = messages[messages.length - 1]?.id
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    // Pin to bottom when already near it (or on first paint, distance ~ 0).
    if (distanceFromBottom < 120) el.scrollTop = el.scrollHeight
  }, [messages.length, lastMessageId])

  function submit(): void {
    const trimmed = text.trim()
    if (!trimmed || sending || disabled) return
    onSend(trimmed)
    setText('')
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  // WhatsApp-style receipt: only the last message I sent carries a status. If the
  // other side has read it we show "Visto", otherwise a muted "Enviado". Read is
  // monotonic (the server marks everything read on open), so the last own message
  // is the meaningful one to label.
  let lastOwnIndex = -1
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]!.side === mySide) {
      lastOwnIndex = i
      break
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-none items-center gap-2.5 border-b border-line bg-surface px-4 py-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Volver a la lista"
            className="-ml-1 flex size-8 flex-none items-center justify-center rounded-lg text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue md:hidden [&_svg]:size-[18px]"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
        )}
        <span className="grid size-9 flex-none place-items-center rounded-full bg-[image:var(--brand-gradient)] text-[11px] font-bold text-white">
          {headerInitials}
        </span>
        <span className="flex min-w-0 flex-col">
          {headerHref ? (
            <Link
              to={headerHref}
              className="truncate text-[13px] font-bold text-text underline-offset-2 hover:text-link hover:underline"
            >
              {headerName}
            </Link>
          ) : (
            <span className="truncate text-[13px] font-bold text-text">{headerName}</span>
          )}
          <span className="truncate text-[10.5px] text-text-3">{headerSubtitle}</span>
        </span>
      </div>

      <div
        ref={scrollRef}
        tabIndex={0}
        aria-label={`Conversación con ${headerName}`}
        className="flex-1 overflow-y-auto px-4 py-4 focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue"
      >
        {messages.length === 0 ? (
          <div className="grid h-full place-items-center px-6 text-center text-[12.5px] leading-relaxed text-text-3">
            {emptyText}
          </div>
        ) : (
          <div
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            aria-label={`Conversación con ${headerName}`}
            className="flex flex-col gap-3.5"
          >
            {messages.map((m, i) => (
              <MessageRow
                key={m.id}
                message={m}
                outgoing={m.side === mySide}
                receipt={i === lastOwnIndex ? (m.read ? 'read' : 'sent') : undefined}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex-none border-t border-line bg-surface px-[14px] pb-3 pt-[10px]">
        <div className="rounded-xl border border-line bg-bg">
          <label className="sr-only" htmlFor="concierge-composer">
            Mensaje
          </label>
          <textarea
            id="concierge-composer"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            className="min-h-[38px] w-full resize-none border-0 bg-transparent px-3 pb-1 pt-2.5 text-[12.5px] leading-normal text-text outline-none placeholder:text-text-3 disabled:cursor-not-allowed"
          />
          <div className="flex items-center gap-1 px-[7px] pb-[7px] pt-[5px]">
            <span className="ml-auto mr-1 text-[10px] text-text-3">Enter para enviar</span>
            <button
              type="button"
              onClick={submit}
              disabled={disabled || sending || text.trim().length === 0}
              className="flex min-h-10 items-center gap-1.5 rounded-lg bg-brand-blue px-3 py-[7px] text-[11.5px] font-semibold text-white transition-colors hover:bg-[#1d4fd1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:size-[14px]"
            >
              {sending ? 'Enviando…' : 'Enviar'}
              {!sending && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4Z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
